import { inject, Injectable } from '@angular/core'
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms'
import { CategoryService } from '@core/auth/services/category.service'
import { categoryRequiredValidator } from '@core/utils/form-utils'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'
import { HULL_SPARES_COVER_PROVISIONS_VISIBILITY } from '@features/certificate/components/hull-spares-cover-provisions/hull-spares-cover-provisions.visibility'
import { CERTIFICATE_CONTRACT_DETAILS_VISIBILITY } from '@features/certificate/components/certificate-contract-details/certificate-contract-details.visibility'
import { CertificateMetaDataForm } from '@features/certificate/models/certificate-meta-data/certificate-meta-data.model'
import { CertificateDetailsForm } from '@features/certificate/models/certificate-details/certificate-details.model'
import { HullSparesCoverProvisionsForm } from '@features/certificate/models/hull-spares-cover-provisions/hull-spares-cover-provisions.model'
import {
  CertificateContractDetailsForm,
  ContractPartyCompany
} from '@features/certificate/models/certificate-contract-details/certificate-contract-details.model'
import { CERTIFICATE_DETAILS_VISIBILITY } from '@features/certificate/components/certificate-details/certificate-details.visibility'
import {
  AircraftTypeLimitRow,
  LiabilityCoverProvisionsForm
} from '@features/certificate/models/liability-cover-provisions/liability-cover-provisions.model'
import { FinalProvisionsForm } from '@features/certificate/models/final-provisions/final-provisions.model'
import { CertificateHolderEmail, CertificateHolderForm } from '@features/certificate-holder/models/certificate-holder.model'
import { CertificateAcrForm } from '@features/certificate/models/certificate-acr/certificate-acr.model'
import { AmountWithCurrencyForm, CertificateFormBundle, FreetextForm } from '@features/certificate/models/certificate-shared.model'
import {
  CertificateApiResponse,
  FurtherInsured,
  TitledEngine,
  TitledEngineForm,
} from '@features/certificate/models/certificate-general-info/certificate-general-info.model'
import {
  AUTHORITIES_SPECIAL_FORMAT,
  CERTIFICATE_AUTHOR,
  CERTIFICATE_STATUS,
  DOCUMENT_STATUS,
  ENGINE_GROSSUP_CLAUSE,
  enumValueFromRaw,
  EU785_COVER_PACKAGE,
  Eu785CoverPackage,
  EXCLUSION_LIABILITY,
  FLEET_LIST_FORMAT,
  OBJECT_TYPE,
  ObjectType,
  SDR_CURRENCY,
  SUBJECT_TO_CERTIFICATION
} from '@features/certificate/models/certificate-select-options.model'
import { titledEngineSerialNumberValidator } from '@features/certificate/validators/titled-engine-serial-number.validator'
import { LIABILITY_COVER_PROVISIONS_VISIBILITY } from '@features/certificate/components/liability-cover-provisions/liability-cover-provisions.visibility'
import { FINAL_PROVISIONS_VISIBILITY } from '@features/certificate/components/final-provisions/final-provisions.visibility'

@Injectable({
  providedIn: 'root'
})
export class CertificateFormService {
  private readonly categoryService = inject(CategoryService)

  public buildMetaDataForm(): FormGroup<CertificateMetaDataForm> {
    return new FormGroup<CertificateMetaDataForm>({
      member: new FormControl({
        uuid: null,
        name: null,
        coverageType: null
      }, { validators: [Validators.required] }),
      certificateCategory: new FormControl(null),
      coverageType: new FormControl(null),
      certificatePublishedBy: new FormControl({
        name: null,
        email: null,
        date: null
      }),
      certificateChangedBy: new FormControl({
        name: null,
        email: null,
        date: null
      }),
      internalComment: new FormControl(null),
      externalComment: new FormControl(null),
    }, { updateOn: 'blur' })
  }

  public buildDetailsForm(): FormGroup<CertificateDetailsForm> {
    return new FormGroup<CertificateDetailsForm>({
      title: new FormControl(null),
      effectiveDate: new FormControl<Date | null>(null),
      subjectToCertification: new FormControl(SUBJECT_TO_CERTIFICATION.NONE),
      subjectToCertificationFreetext: this.buildFreetextGroup(this.categoryRequired(
        CERTIFICATE_DETAILS_VISIBILITY,
        'subjectToCertificationFreetext',
        this.freetextGate()
      )),
      notToBeRenewed: new FormControl(false, { updateOn: 'change' }),
      authoritiesSpecialFormat: new FormControl(AUTHORITIES_SPECIAL_FORMAT.NONE),
      certificateAuthor: new FormControl(CERTIFICATE_AUTHOR.ALBATROS, { validators: [Validators.required] }),
      linkedCertificates: new FormControl(null),
      status: new FormControl(CERTIFICATE_STATUS.DRAFT),
      documentStatus: new FormControl(DOCUMENT_STATUS.NONE),
      certificateIdValue: new FormControl(null),
      importedDoc: new FormControl(null),
      policyId: new FormControl(null, { validators: [Validators.required] }),
      periodId: new FormControl(null, { validators: [Validators.required] }),
      associatedCompanies: new FormControl(false, { updateOn: 'change' }),
    }, { updateOn: 'blur' })
  }

  public buildContractDetailsForm(): FormGroup<CertificateContractDetailsForm> {
    return new FormGroup<CertificateContractDetailsForm>({
      furtherInsured: new FormControl<FurtherInsured[] | null>([]),
      contractParties: new FormControl(null),
      contractPartiesEuat: new FormControl<ContractPartyCompany[] | null>([]),
      contracts: new FormControl(null),
      avn67B: new FormControl(false, { updateOn: 'change' }),
      avn67C: new FormControl(false, { updateOn: 'change' }),
      designatedContractParty: new FormControl(null, {
        validators: [
          this.categoryRequired(
            CERTIFICATE_CONTRACT_DETAILS_VISIBILITY,
            'designatedContractParty',
            control => control.parent?.get('avn67C')?.value === true
          )
        ]
      }),
    }, { updateOn: 'blur' })
  }

  public buildHullSparesCoverProvisionsForm(): FormGroup<HullSparesCoverProvisionsForm> {
    return new FormGroup<HullSparesCoverProvisionsForm>({
      certificateId: new FormControl(null),
      objectType: new FormControl<ObjectType | null>(null, { validators: [this.categoryRequired(HULL_SPARES_COVER_PROVISIONS_VISIBILITY, 'objectType')] }),
      equipmentTypeFreetextCheckbox: new FormControl(false, { updateOn: 'change' }),
      equipmentTypeFreetext: new FormControl(null),
      aircraftType: new FormControl<string[] | null>([]),
      aircraftTypeForCertificates: new FormControl(null),
      aircraftSerialNumber: new FormControl(null, {
        validators: [this.categoryRequired(
          HULL_SPARES_COVER_PROVISIONS_VISIBILITY,
          'aircraftSerialNumber',
          this.objectTypeGate(false, OBJECT_TYPE.AIRCRAFT)
        )]
      }),
      aircraftRegistration: new FormControl(null, {
        validators: [this.categoryRequired(
          HULL_SPARES_COVER_PROVISIONS_VISIBILITY,
          'aircraftRegistration',
          this.objectTypeGate(false, OBJECT_TYPE.AIRCRAFT)
        )]
      }),
      titledEngines: new FormArray<FormGroup<TitledEngineForm>>([]),
      engineGrossupClause: new FormControl(ENGINE_GROSSUP_CLAUSE.NONE),
      equipmentType: new FormControl(null),
      equipmentTypeForCertificate: new FormControl(null),
      equipmentSerialNumber: new FormControl(null, {
        validators: [this.categoryRequired(
          HULL_SPARES_COVER_PROVISIONS_VISIBILITY,
          'equipmentSerialNumber',
          this.objectTypeGate(false, OBJECT_TYPE.ENGINE, OBJECT_TYPE.EQUIPMENT)
        )]
      }),
      leaseValues: new FormArray<FormGroup<AmountWithCurrencyForm>>([]),
      leaseValueTba: new FormControl(false, { updateOn: 'change' }),
      leaseValuesString: new FormControl(null),
      hullAllRiskDeductibleAmount: this.buildAmountWithCurrencyGroup(this.categoryRequired(
        HULL_SPARES_COVER_PROVISIONS_VISIBILITY,
        'hullAllRiskDeductibleAmount',
        this.checkboxGate('hullAllRiskDeductible')
      )),
      remainingDeductibleAmount: this.buildAmountWithCurrencyGroup(),
      hullAllRiskDeductible: new FormControl(false, { updateOn: 'change' }),
      fleetOfAircraft: new FormControl(false, { updateOn: 'change' }),
      addFleetListAsAttachment: new FormControl(false, { updateOn: 'change' }),
      columnConfiguration: new FormControl(
        FLEET_LIST_FORMAT.DEFAULT,
        { validators: [this.categoryRequired(HULL_SPARES_COVER_PROVISIONS_VISIBILITY, 'columnConfiguration')] }
      ),
      filterByAircraftType: new FormControl(false, { updateOn: 'change' }),
      aircraftTypeFilter: new FormControl<string[] | null>([]),
      includePlannedAircraft: new FormControl(false, { updateOn: 'change' }),
      hullInsurance: this.buildFreetextGroup(this.categoryRequired(HULL_SPARES_COVER_PROVISIONS_VISIBILITY, 'hullInsurance', this.freetextGate())),
      hullWarInsurance: this.buildFreetextGroup(this.categoryRequired(HULL_SPARES_COVER_PROVISIONS_VISIBILITY, 'hullWarInsurance', this.freetextGate())),
      sparesAllRiskInsurance: this.buildFreetextGroup(this.categoryRequired(
        HULL_SPARES_COVER_PROVISIONS_VISIBILITY,
        'sparesAllRiskInsurance',
        this.freetextGate()
      )),
      includeEngineTestRunOnCertificate: new FormControl(false, { updateOn: 'change' }),
      includeSparesDeductibleOnCertificate: new FormControl(false, { updateOn: 'change' }),
      waiverOfSubrogation: this.buildFreetextGroup(this.categoryRequired(HULL_SPARES_COVER_PROVISIONS_VISIBILITY, 'waiverOfSubrogation', this.freetextGate())),
      noLiabilityForPremiums: this.buildFreetextGroup(this.categoryRequired(
        HULL_SPARES_COVER_PROVISIONS_VISIBILITY,
        'noLiabilityForPremiums',
        this.freetextGate()
      )),
      noticeOfCancellationInclWar: new FormControl(false, { updateOn: 'change' }),
      noticeOfCancellationNoWar: this.buildFreetextGroup(this.categoryRequired(
        HULL_SPARES_COVER_PROVISIONS_VISIBILITY,
        'noticeOfCancellationNoWar',
        this.freetextGate()
      )),
      breachOfWarranty: this.buildFreetextGroup(this.categoryRequired(HULL_SPARES_COVER_PROVISIONS_VISIBILITY, 'breachOfWarranty', this.freetextGate())),
      additionalInsured: this.buildFreetextGroup(this.categoryRequired(HULL_SPARES_COVER_PROVISIONS_VISIBILITY, 'additionalInsured', this.freetextGate())),
      engineGrossUpClauseFreetextCheckbox: new FormControl(false, { updateOn: 'change' }),
      engineGrossUpClauseFreetext: new FormControl(null),
    }, { updateOn: 'blur' })
  }

  public buildLiabilityCoverProvisionsForm(): FormGroup<LiabilityCoverProvisionsForm> {
    return new FormGroup<LiabilityCoverProvisionsForm>({
      certificateId: new FormControl(null),
      cslOnCertificateAmount: this.buildAmountWithCurrencyGroup(),
      avn52eTotalLimitAmount: this.buildAmountWithCurrencyGroup(),
      groundingLiabilityAmount: this.buildAmountWithCurrencyGroup(),
      personalInjuryAmount: this.buildAmountWithCurrencyGroup(),
      avn59Amount: this.buildAmountWithCurrencyGroup(),
      passengerBaggageDeductibleAmount: this.buildAmountWithCurrencyGroup(),
      cargoAndMailDeductibleAmount: this.buildAmountWithCurrencyGroup(),
      sublimitLiquidLiabilityAmount: this.buildAmountWithCurrencyGroup(),
      deductibleAllLossesAmount: this.buildAmountWithCurrencyGroup(),
      standardCoverPackage: new FormControl(false, { updateOn: 'change' }),
      standardCoverPackageAirline: new FormControl(false, { updateOn: 'change' }),
      standardCoverPackageMro: new FormControl(false, { updateOn: 'change' }),
      eu785CoverPackage: new FormControl(EU785_COVER_PACKAGE.NONE),
      sdrAmount: this.buildSdrAmountGroup(),
      aircraftThirdPartyLiability: new FormControl(false, { updateOn: 'change' }),
      hangarkeepersLiability: new FormControl(false, { updateOn: 'change' }),
      avn52ePrimaryLimit: new FormControl(false, { updateOn: 'change' }),
      productsLiability: new FormControl(false, { updateOn: 'change' }),
      passengerLiability: new FormControl(false, { updateOn: 'change' }),
      productsCompletedOperationsLiability: new FormControl(false, { updateOn: 'change' }),
      personalInjury: new FormControl(false, { updateOn: 'change' }),
      motorLiabilityNonRegistered: new FormControl(false, { updateOn: 'change' }),
      cargoAndMailLiability: new FormControl(false, { updateOn: 'change' }),
      motorLiabilityRegistered: new FormControl(false, { updateOn: 'change' }),
      aviationPremiseLiability: new FormControl(false, { updateOn: 'change' }),
      aviationGeneralThirdPartyLiability: new FormControl(false, { updateOn: 'change' }),
      contractualLiability: new FormControl(false, { updateOn: 'change' }),
      additionalFreetextLiability: this.buildFreetextGroup(this.categoryRequired(
        LIABILITY_COVER_PROVISIONS_VISIBILITY,
        'additionalFreetextLiability',
        this.freetextGate()
      )),
      bhvPrimary: new FormControl(false, { updateOn: 'change' }),
      bhvPrimaryAmount: this.buildAmountWithCurrencyGroup(this.categoryRequired(
        LIABILITY_COVER_PROVISIONS_VISIBILITY,
        'bhvPrimaryAmount',
        this.checkboxGate('bhvPrimary')
      )),
      bhvPrimaryFreetext: this.buildFreetextGroup(this.categoryRequired(
        LIABILITY_COVER_PROVISIONS_VISIBILITY,
        'bhvPrimaryFreetext',
        this.freetextGate()
      )),
      bhvExcessOnly: new FormControl(false, { updateOn: 'change' }),
      additionalInsured: this.buildFreetextGroup(this.categoryRequired(LIABILITY_COVER_PROVISIONS_VISIBILITY, 'additionalInsured', this.freetextGate())),
      primaryClause: this.buildFreetextGroup(this.categoryRequired(LIABILITY_COVER_PROVISIONS_VISIBILITY, 'primaryClause', this.freetextGate())),
      severabilityOfInterest: this.buildFreetextGroup(this.categoryRequired(
        LIABILITY_COVER_PROVISIONS_VISIBILITY,
        'severabilityOfInterest',
        this.freetextGate()
      )),
      noLiabilityForPremiums: this.buildFreetextGroup(this.categoryRequired(
        LIABILITY_COVER_PROVISIONS_VISIBILITY,
        'noLiabilityForPremiums',
        this.freetextGate()
      )),
      breachOfWarranty: this.buildFreetextGroup(this.categoryRequired(LIABILITY_COVER_PROVISIONS_VISIBILITY, 'breachOfWarranty', this.freetextGate())),
      noticeOfCancellationInclWar: new FormControl(false, { updateOn: 'change' }),
      noticeOfCancellationNoWar: this.buildFreetextGroup(this.categoryRequired(
        LIABILITY_COVER_PROVISIONS_VISIBILITY,
        'noticeOfCancellationNoWar',
        this.freetextGate()
      )),
      contingencyCodeshare: new FormControl(false, { updateOn: 'change' }),
      waiverOfSubrogation: new FormControl(false, { updateOn: 'change' }),
      additionalFreetext: this.buildFreetextGroup(this.categoryRequired(LIABILITY_COVER_PROVISIONS_VISIBILITY, 'additionalFreetext', this.freetextGate())),
      liabilityLimitsPerAircraftType: new FormControl(false, { updateOn: 'change' }),
      liabilityLimitAmountAircraft: new FormArray<FormGroup<AmountWithCurrencyForm>>([]),
      liabilityAircraftType: new FormArray<FormControl<string>>([]),
      liabilityAvn52eLimitsFleetList: new FormControl(false, { updateOn: 'change' }),
      liabilityAvn52eVariousLimits: new FormControl(false, { updateOn: 'change' }),
    }, { updateOn: 'blur' })
  }

  public buildFinalProvisionsForm(): FormGroup<FinalProvisionsForm> {
    return new FormGroup<FinalProvisionsForm>({
      certificateId: new FormControl(null),
      exclusionLiability: new FormControl(EXCLUSION_LIABILITY.NONE),
      exclusionWorkmensCompensation: new FormControl(false, { updateOn: 'change' }),
      exclNeglWilfulMisconduct: this.buildFreetextGroup(this.categoryRequired(FINAL_PROVISIONS_VISIBILITY, 'exclNeglWilfulMisconduct', this.freetextGate())),
      freetext: this.buildFreetextGroup(this.categoryRequired(FINAL_PROVISIONS_VISIBILITY, 'freetext', this.freetextGate())),
      airBrokerIncluded: new FormControl(false, { updateOn: 'change' }),
      airBrokerName: new FormControl(
        null,
        { validators: [this.categoryRequired(FINAL_PROVISIONS_VISIBILITY, 'airBrokerName', this.checkboxGate('airBrokerIncluded'))] }
      ),
      includeCutThroughClause: new FormControl(false, { updateOn: 'change' }),
      includeBrokerLetterOfUndertaking: new FormControl(false, { updateOn: 'change' }),
      includeInsurerSplit: new FormControl(false, { updateOn: 'change' }),
      boeingDefinition: new FormControl(false, { updateOn: 'change' }),
      exclusionManufacturerRepairer: new FormControl(false, { updateOn: 'change' }),
    }, { updateOn: 'blur' })
  }

  public buildCertificateHolderForm(): FormGroup<CertificateHolderForm> {
    return new FormGroup<CertificateHolderForm>({
      uuid: new FormControl(null),
      companyName: new FormControl(null),
      country: new FormControl(null),
      freetext: new FormControl(null),
      usedIn: new FormControl([]),
      recipientAddresses: new FormArray<FormControl<CertificateHolderEmail>>([]),
    })
  }

  public buildAcrForm(): FormGroup<CertificateAcrForm> {
    return new FormGroup<CertificateAcrForm>({
      recipientName: new FormControl(null),
      recipientAddress: new FormControl(null),
      recipientCity: new FormControl(null),
      recipientCountry: new FormControl(null),
      recipientPostalCode: new FormControl(null),
    })
  }

  public patchAllForms(response: CertificateApiResponse, forms: CertificateFormBundle): void {
    this.patchMetaDataForm(response, forms)
    this.patchDetailsForm(response, forms)
    this.patchContractDetailsForm(response, forms)
    this.patchHullSparesCoverProvisionsForm(response, forms)
    this.patchLiabilityCoverProvisionsForm(response, forms)
    this.patchFinalProvisionsForm(response, forms)
  }

  /** Replaces the limits per aircraft type; the aircraft types and their limits are kept in two parallel arrays. */
  public setAircraftTypeLimits(form: FormGroup<LiabilityCoverProvisionsForm>, rows: AircraftTypeLimitRow[]): void {
    const limits = form.controls.liabilityLimitAmountAircraft
    const types = form.controls.liabilityAircraftType
    limits.clear({ emitEvent: false })
    types.clear({ emitEvent: false })
    for (const row of rows) {
      const group = this.buildAmountWithCurrencyGroup()
      group.setValue({ amount: row.amount, currency: row.currency })
      limits.push(group, { emitEvent: false })
      types.push(new FormControl(row.aircraftType, { nonNullable: true }), { emitEvent: false })
    }
    // One emission for the whole list instead of one per row.
    limits.updateValueAndValidity()
    types.updateValueAndValidity()
  }

  public buildAmountWithCurrencyGroup(required: boolean | ValidatorFn = false): FormGroup<AmountWithCurrencyForm> {
    const validator = this.resolveRequiredValidator(required)
    return new FormGroup<AmountWithCurrencyForm>({
      amount: new FormControl(null, { validators: [validator] }),
      currency: new FormControl(null, { validators: [validator] }),
    })
  }

  public buildFreetextGroup(required: boolean | ValidatorFn = false): FormGroup<FreetextForm> {
    return new FormGroup<FreetextForm>({
      uuid: new FormControl(null),
      checkbox: new FormControl(false, { updateOn: 'change' }),
      freetextCheckbox: new FormControl(false, { updateOn: 'change' }),
      freetext: new FormControl(null, { validators: [this.resolveRequiredValidator(required)] }),
    })
  }

  public buildTitledEngineGroup(): FormGroup<TitledEngineForm> {
    return new FormGroup<TitledEngineForm>({
      serialNumber: new FormControl(null, {
        validators: [
          this.categoryRequired(
            HULL_SPARES_COVER_PROVISIONS_VISIBILITY,
            'titledEngines',
            this.objectTypeGate(false, OBJECT_TYPE.AIRCRAFT)
          ),
          titledEngineSerialNumberValidator()
        ]
      }),
      type: new FormControl(null),
    })
  }

  private categoryRequired<M extends Record<string, readonly CertificateCategory[] | undefined>>(
    map: M,
    field: keyof M,
    extraCondition?: (control: AbstractControl) => boolean
  ): ValidatorFn {
    const visibleCategories = map[field] ?? []
    return categoryRequiredValidator(
      () => this.categoryService.activeCertificateCategory(),
      visibleCategories,
      extraCondition
    )
  }

  private freetextGate(): (control: AbstractControl) => boolean {
    return control => control.parent?.get('freetextCheckbox')?.value === true
  }

  private checkboxGate(checkboxControlName: string): (control: AbstractControl) => boolean {
    return control => control.root.get(checkboxControlName)?.value === true
  }

  private buildSdrAmountGroup(): FormGroup<AmountWithCurrencyForm> {
    const group = this.buildAmountWithCurrencyGroup(this.categoryRequired(
      LIABILITY_COVER_PROVISIONS_VISIBILITY,
      'sdrAmount',
      this.eu785ActiveGate()
    ))
    group.controls.currency.setValue(SDR_CURRENCY)
    return group
  }

  private eu785ActiveGate(): (control: AbstractControl) => boolean {
    return control => {
      const value = control.root.get('eu785CoverPackage')?.value as Eu785CoverPackage | null | undefined
      return value !== null && value !== undefined && value !== EU785_COVER_PACKAGE.NONE
    }
  }

  private objectTypeGate(defaultForOtherCategories: boolean, ...allowedObjectTypes: ObjectType[]): (control: AbstractControl) => boolean {
    return control => {
      if (this.categoryService.activeCertificateCategory() !== CertificateCategory.LEASE) {
        return defaultForOtherCategories
      }
      const objectType = control.root.get('objectType')?.value as ObjectType | null | undefined
      return objectType !== null && objectType !== undefined && allowedObjectTypes.includes(objectType)
    }
  }

  private resolveRequiredValidator(required: boolean | ValidatorFn): ValidatorFn {
    if (typeof required === 'function') {
      return required
    }
    return required ? Validators.required : Validators.nullValidator
  }

  private patchMetaDataForm(response: CertificateApiResponse, forms: CertificateFormBundle): void {
    forms.metaData.patchValue({
      member: {
        uuid: response.member?.uuid ?? null,
        name: response.member?.name ?? '',
        coverageType: response.member?.coverageType ?? null
      },
      certificateCategory: response.certificateCategory,
      coverageType: response.member?.coverageType ?? '',
      certificateChangedBy: response.certificateChangedBy,
      certificatePublishedBy: response.certificatePublishedBy,
      internalComment: response.internalComment,
      externalComment: response.externalComment,
    })
  }

  private patchDetailsForm(response: CertificateApiResponse, forms: CertificateFormBundle): void {
    forms.details.patchValue({
      title: response.certificateTitle,
      effectiveDate: (response.effectiveDate === null || response.effectiveDate === '') ? null : new Date(response.effectiveDate),
      subjectToCertification: enumValueFromRaw(SUBJECT_TO_CERTIFICATION, response.subjectToCertification),
      subjectToCertificationFreetext: this.mapCoverFreetext(response.subjectToCertificationFreetext),
      notToBeRenewed: response.notToBeRenewed,
      authoritiesSpecialFormat: enumValueFromRaw(AUTHORITIES_SPECIAL_FORMAT, response.specialFormat),
      certificateAuthor: enumValueFromRaw(CERTIFICATE_AUTHOR, response.certificateAuthor),
      linkedCertificates: response.linkedCertificates,
      status: response.certificateStatus ?? response.status,
      documentStatus: response.documentStatus,
      certificateIdValue: response.certificateIdValue ?? null,
      importedDoc: response.importedDoc ?? null,
      policyId: response.policy?.uuid ?? null,
      periodId: response.period?.uuid ?? null,
      associatedCompanies: response.associatedCompanies
    })

    if (forms.period && response.period) {
      forms.period.patchValue({
        uuid: response.period.uuid,
        name: response.period.name ?? '',
        periodFrom: response.period.periodFrom ?? null,
        periodTo: response.period.perdioTo ?? null,
      })
    }

    if (forms.policy && response.policy) {
      forms.policy.patchValue({
        uuid: response.policy.uuid,
        status: response.policy.policyStatus,
      })
      forms.policy.controls.coverageType.patchValue({
        localValue: response.policy.coverageInsuranceType,
      })
    }
  }

  private patchContractDetailsForm(response: CertificateApiResponse, forms: CertificateFormBundle): void {
    forms.contractDetails.patchValue({
      furtherInsured: (response.furtherInsured ?? []).map(item => ({
        memberId: item.memberId,
        company: item.company,
        policyId: item.policyId ?? null
      })),
      contractParties: response.contractParties,
      contractPartiesEuat: response.contractPartiesEuat ?? [],
      contracts: response.contracts,
      avn67B: response.avn67b,
      avn67C: response.avn67c,
      designatedContractParty: response.avn67cDesignatedContractParty,
    })
  }

  private patchHullSparesCoverProvisionsForm(response: CertificateApiResponse, forms: CertificateFormBundle): void {
    const leaseValuesArray = forms.hullSparesCoverProvisions.controls.leaseValues
    leaseValuesArray.clear()
    this.buildLeaseValuesArray(leaseValuesArray, response.leaseValues)
    const titledEnginesArray = forms.hullSparesCoverProvisions.controls.titledEngines
    titledEnginesArray.clear()
    this.buildTitledEnginesArray(titledEnginesArray, response.titledEngineValues)
    const aircraftTypeArray = forms.hullSparesCoverProvisions.controls.aircraftType
    const rawAircraftType = response.aircraftType
    if (Array.isArray(rawAircraftType)) {
      const values = rawAircraftType
        .filter(Boolean) as string[]
      aircraftTypeArray.setValue(values)
    } else if (typeof rawAircraftType === 'string') {
      aircraftTypeArray.setValue([rawAircraftType])
    }

    const aircraftTypeFilterArray = forms.hullSparesCoverProvisions.controls.aircraftTypeFilter
    const rawAircraftTypeFilter = response.aircraftTypes
    if (Array.isArray(rawAircraftTypeFilter)) {
      const values = rawAircraftTypeFilter
        .map(item => typeof item === 'string' ? item : item?.description)
        .filter(Boolean) as string[]
      aircraftTypeFilterArray.setValue(values)
    }
    forms.hullSparesCoverProvisions.patchValue({
      objectType: enumValueFromRaw(OBJECT_TYPE, response.objectType),
      equipmentTypeFreetextCheckbox: response.equipmentTypeFreetextCheckbox ?? false,
      equipmentTypeFreetext: response.equipmentTypeFreetext ?? null,
      aircraftTypeForCertificates: response.aircraftTypeForCertificate ?? null,
      aircraftSerialNumber: response.aircraftSerialNumber ?? null,
      aircraftRegistration: response.aircraftRegistration ?? null,
      engineGrossupClause: enumValueFromRaw(ENGINE_GROSSUP_CLAUSE, response.engineGrossUpClause),
      engineGrossUpClauseFreetextCheckbox: response.engineGrossUpClauseFreetextCheckbox ?? false,
      engineGrossUpClauseFreetext: response.engineGrossUpClauseFreetext ?? null,
      equipmentType: response.equipmentType ?? null,
      equipmentTypeForCertificate: response.equipmentTypeForCertificate ?? null,
      equipmentSerialNumber: response.equipmentSerialNumber ?? null,
      leaseValueTba: response.leaseValueTba ?? null,
      hullAllRiskDeductibleAmount: this.mapAmountWithCurrency(response.hullAllRiskDeductibleAmount),
      remainingDeductibleAmount: this.mapAmountWithCurrency(response.remainingDeductible),
      hullAllRiskDeductible: response.hullAllRiskDeductible ?? false,
      fleetOfAircraft: response.fleetOfAircraft ?? false,
      addFleetListAsAttachment: response.fleetListAsAttachment ?? false,
      columnConfiguration: enumValueFromRaw(FLEET_LIST_FORMAT, response.fleetListFormat) ?? FLEET_LIST_FORMAT.DEFAULT,
      filterByAircraftType: response.filterByAircraftType ?? false,
      includePlannedAircraft: response.includePlannedAircraft ?? false,
      hullInsurance: this.mapCoverFreetext(response.hullInsurance),
      hullWarInsurance: this.mapCoverFreetext(response.hullWarInsurance),
      sparesAllRiskInsurance: this.mapCoverFreetext(response.sparesAllRiskInsurance),
      includeEngineTestRunOnCertificate: response.sparesAllRiskInsurance?.includeEngineTestRunOnCertificate ?? false,
      includeSparesDeductibleOnCertificate: response.sparesAllRiskInsurance?.includeSparesDeductibleOnCertificate ?? false,
      waiverOfSubrogation: this.mapCoverFreetext(response.hullCoverAndProvisionsWaiverOfSubrogation),
      noLiabilityForPremiums: this.mapCoverFreetext(response.hullCoverAndProvisionsNoLiabilityForPremiums),
      noticeOfCancellationInclWar: (response.contractNoticeOfCancellation
        ?? response.noticeOfCancellation)?.noticeOfCancellationInclWar ?? false,
      noticeOfCancellationNoWar: this.mapNoticeOfCancellation(response.contractNoticeOfCancellation
        ?? response.noticeOfCancellation),
      breachOfWarranty: this.mapCoverFreetext(response.hullCoverAndProvisionsBreachOfWarranty),
      additionalInsured: this.mapCoverFreetext(response.contractAdditionalInsured ?? response.additionalInsured, response.freetextCheckbox, response.freetext),
    })
  }

  private patchLiabilityCoverProvisionsForm(response: CertificateApiResponse, forms: CertificateFormBundle): void {
    const rawLimits = response.liabilityLimitAmountAircraft ?? []
    const rawTypes = response.liabilityAircraftType ?? []
    this.setAircraftTypeLimits(forms.liabilityCoverProvisions, Array.from(
      { length: Math.max(rawLimits.length, rawTypes.length) },
      (_, index) => ({
        aircraftType: rawTypes[index] ?? '',
        amount: rawLimits[index]?.amount ?? null,
        currency: rawLimits[index]?.currency ?? null
      })
    ))

    forms.liabilityCoverProvisions.patchValue({
      cslOnCertificateAmount: this.mapAmountWithCurrency(response.liabilityCoverCslOnCertificate ?? response.cslOnCertificate),
      avn52eTotalLimitAmount: this.mapAmountWithCurrency(response.liabilityCoverAvn52eTotalLimit ?? response.avn52eTotalLimit),
      groundingLiabilityAmount: this.mapAmountWithCurrency(response.groundingLiabilityAmount),
      personalInjuryAmount: this.mapAmountWithCurrency(response.liabilityCoverPersonalInjuryAmount
        ?? response.personalInjuryAmount),
      avn59Amount: this.mapAmountWithCurrency(response.liabilityCoverAvn59amount ?? response.avn59amount),
      passengerBaggageDeductibleAmount: this.mapAmountWithCurrency(response.passengerBaggageDeductibleAmount),
      cargoAndMailDeductibleAmount: this.mapAmountWithCurrency(response.cargoAndMailDeductibleAmount),
      sublimitLiquidLiabilityAmount: this.mapAmountWithCurrency(response.sublimitLiquidLiabilityAmount),
      deductibleAllLossesAmount: this.mapAmountWithCurrency(response.deductibleAllLossesAmount),
      standardCoverPackage: response?.standardCoverPackage ?? null,
      standardCoverPackageAirline: response.standardCoverPackageAirline ?? null,
      standardCoverPackageMro: response.standardCoverPackageMro ?? null,
      eu785CoverPackage: enumValueFromRaw(EU785_COVER_PACKAGE, response.eu785CoverPackage),
      sdrAmount: { amount: response.sdrAmount?.amount ?? null, currency: SDR_CURRENCY },
      aircraftThirdPartyLiability: response.aircraftThirdPartyLiability ?? null,
      avn52ePrimaryLimit: response.liabilityCoverAvn52ePrimaryLimit ?? response.avn52ePrimaryLimit ?? null,
      passengerLiability: response.passengerLiability ?? null,
      personalInjury: response.liabilityCoverPersonalInjury ?? response.personalInjury ?? null,
      cargoAndMailLiability: response.cargoAndMailLiability ?? null,
      hangarkeepersLiability: response.hangarkeepersLiability ?? null,
      productsLiability: response.productsLiability ?? null,
      productsCompletedOperationsLiability: response.productsCompletedOperationsLiability ?? null,
      motorLiabilityNonRegistered: response.motorLiabilityNonRegistered ?? null,
      motorLiabilityRegistered: response.motorLiabilityRegistered ?? null,
      aviationPremiseLiability: response.aviationPremiseLiability ?? null,
      aviationGeneralThirdPartyLiability: response.aviationGeneralThirdPartyLiability ?? null,
      contractualLiability: response.contractualLiability ?? null,
      additionalFreetextLiability: this.mapCheckboxFreetext(
        response.liabilityCoverAdditionalFreetextLiabilityCheckbox
        ?? response.additionalFreetextLiabilityCheckbox,
        response.liabilityCoverAdditionalFreetextLiability ?? response.additionalFreetextLiability
      ),
      bhvPrimary: !!(response.bhvPrimaryAmount ?? null),
      bhvPrimaryAmount: this.mapAmountWithCurrency(response.bhvPrimaryAmount),
      bhvPrimaryFreetext: this.mapCheckboxFreetext(
        response.bhvPrimaryFreetextCheckbox ?? null,
        response.bhvPrimaryFreetext ?? null
      ),
      bhvExcessOnly: response.bhvExcessOnly ?? null,
      additionalInsured: this.mapCoverFreetext(response.liabilityCoverAdditionalInsured ?? response.additionalInsured),
      primaryClause: this.mapCoverFreetext(response.liabilityCoverPrimaryClause ?? response.primaryClause),
      severabilityOfInterest: this.mapCoverFreetext(response.liabilityCoverSeverabilityOfInterest
        ?? response.severabilityOfInterest),
      noLiabilityForPremiums: this.mapCoverFreetext(response.liabilityCoverAndProvisionsNoLiabilityForPremiums),
      breachOfWarranty: this.mapCoverFreetext(response.liabilityCoverAndProvisionsBreachOfWarranty),
      noticeOfCancellationInclWar: (response.liabilityCoverCoverNoticeOfCancellationFreetext
        ?? response.coverNoticeOfCancellationFreetext)?.noticeOfCancellationInclWar ?? false,
      noticeOfCancellationNoWar: this.mapNoticeOfCancellation(response.liabilityCoverCoverNoticeOfCancellationFreetext
        ?? response.coverNoticeOfCancellationFreetext
        ?? null),
      contingencyCodeshare: response.contingencyCodeshare ?? null,
      waiverOfSubrogation: response.liabilityCoverAndProvisionsVaiverOfSubrogation ?? null,
      additionalFreetext: this.mapCheckboxFreetext(response.liabilityCoverAdditionalFreetextCheckbox
        ?? response.additionalFreetextCheckbox
        ?? null, response.liabilityCoverAdditionalFreetext ?? response.additionalFreetext ?? null),
      liabilityLimitsPerAircraftType: response.liabilityLimitsPerAircraftType ?? null,
      liabilityAvn52eLimitsFleetList: response.liabilityAvn52eLimitsFleetList ?? null,
      liabilityAvn52eVariousLimits: response.liabilityAvn52eVariousLimits ?? null,
    })
  }

  private patchFinalProvisionsForm(response: CertificateApiResponse, forms: CertificateFormBundle): void {
    forms.finalProvisions.patchValue({
      exclusionLiability: enumValueFromRaw(
        EXCLUSION_LIABILITY,
        response.finalProvisionExclusionLiability ?? response.exclusionLiability
      ),
      exclusionWorkmensCompensation: response.exclusionWorkmensCompensation ?? null,
      exclNeglWilfulMisconduct: this.mapCheckboxFreetext(
        response.finalProvisionExclusionNegligenceFreetextCheckbox
        ?? response.exclusionNegligenceFreetextCheckbox,
        response.finalProvisionExclusionNegligenceFreetext ?? response.exclusionNegligenceFreetext
      ),
      freetext: this.mapCheckboxFreetext(
        response.finalProvisionFreetextCheckbox,
        response.finalProvisionFreetext
      ),
      airBrokerIncluded: response.airBrokerIncluded ?? null,
      airBrokerName: response.airBrokerName ?? null,
      includeCutThroughClause: response.finalProvisionCutThroughClause ?? response.cutThroughClause ?? null,
      includeBrokerLetterOfUndertaking: response.brokerLetter ?? null,
      includeInsurerSplit: response.finalProvisionInsuredSplit ?? response.insuredSplit ?? null,
      boeingDefinition: response.boingDefinition ?? null,
      exclusionManufacturerRepairer: response.exclusionManufacturerRepairer ?? null,
    })
  }

  private mapAmountWithCurrency(value: { amount: number | null; currency: string | null } | null | undefined
  ): { amount: number | null; currency: string | null } {
    return value ? { amount: value.amount ?? null, currency: value.currency ?? null } : { amount: null, currency: null }
  }

  private mapCoverFreetext(
    section: (boolean | {
      uuid?: string | null;
      cover?: boolean | null;
      coverFreetextCheckbox?: boolean | null;
      coverFreetext?: string | null
    }) | null | undefined,
    freetextCheckbox?: boolean | null | undefined,
    freetext?: string | null | undefined
  ): { uuid: string | null; checkbox: boolean; freetextCheckbox: boolean; freetext: string | null } {
    if (typeof section === 'boolean') {
      return { uuid: null, checkbox: section, freetextCheckbox: freetextCheckbox ?? false, freetext: freetext ?? '' }
    }
    return section
      ? {
        uuid: section.uuid ?? null,
        checkbox: section.cover ?? false,
        freetextCheckbox: section.coverFreetextCheckbox ?? freetextCheckbox ?? false,
        freetext: section.coverFreetext ?? freetext ?? null
      }
      : { uuid: null, checkbox: false, freetextCheckbox: freetextCheckbox ?? false, freetext: freetext ?? null }
  }

  private mapNoticeOfCancellation(section: {
    uuid?: string | null;
    noticeOfCancellationNoWar?: boolean | null;
    noticeOfCancellationFreetextCheckbox?: boolean | null;
    noticeOfCancellationFreetext?: string | null
  } | null | undefined): { uuid: string | null; checkbox: boolean; freetextCheckbox: boolean; freetext: string | null } {
    return section
      ? {
        uuid: section.uuid ?? null,
        checkbox: section.noticeOfCancellationNoWar ?? false,
        freetextCheckbox: section.noticeOfCancellationFreetextCheckbox ?? false,
        freetext: section.noticeOfCancellationFreetext ?? null
      }
      : { uuid: null, checkbox: false, freetextCheckbox: false, freetext: null }
  }

  private mapCheckboxFreetext(checkbox: boolean | null | undefined, freetext: string | null | undefined): {
    checkbox: boolean;
    freetextCheckbox: boolean;
    freetext: string | null
  } {
    return { checkbox: checkbox ?? false, freetextCheckbox: checkbox ?? false, freetext: freetext ?? null }
  }

  private buildLeaseValuesArray(
    leaseValuesArray: FormArray<FormGroup<AmountWithCurrencyForm>>,
    leaseValues: { amount: number; currency: string | null }[] | null | undefined
  ): void {
    if (leaseValues) {
      for (const entry of leaseValues) {
        const group = this.buildAmountWithCurrencyGroup()
        group.patchValue({ amount: entry.amount, currency: entry.currency })
        leaseValuesArray.push(group)
      }
    }
  }

  private buildTitledEnginesArray(
    titledEnginesArray: FormArray<FormGroup<TitledEngineForm>>,
    titledEngines: TitledEngine[] | null | undefined
  ): void {
    if (titledEngines) {
      for (const entry of titledEngines) {
        const group = this.buildTitledEngineGroup()
        // Attach first: the serial number's required gate reads `objectType` from the form root,
        // which a detached group cannot see. Patching afterwards revalidates against the real root.
        titledEnginesArray.push(group)
        group.patchValue({
          serialNumber: entry.serialNumber,
          type: entry.type
        })
      }
    }
  }

}
